/**
 * In-content links — issue 591.
 *
 * Authored prose reaches the DOM through RichText as plain anchors, so a
 * click on one was a full page reload, then a second hop through
 * LegacyDetailRedirect, arriving with no router state — the back link and
 * the breadcrumb lost the list the reader came from. This is the pure half
 * of the fix: given an anchor's href, the in-app path it means, or null when
 * the click should be left to the browser (another origin, a download, a
 * hash, a file).
 *
 * Two schemes are understood. The modern query form is returned as it is.
 * The ingest-era path form — `/<segment>/<rulebook>--<id>/<slug>--<id>/`,
 * the one LegacyDetailRedirect rewrites after a reload — is rewritten HERE
 * to the query form, so the click is one client-side navigation and no
 * redirect hop. The rewrite is the same one the redirect performs: strip the
 * `--<id>` suffix from both segments, `?slug=` and `?rulebook=`.
 */
const LEGACY_ID = /--\d+$/;

/** The in-app path an authored href means, or null to let the browser have it. */
export function resolveContentHref(href, origin = (typeof window !== 'undefined' ? window.location.origin : 'http://localhost')) {
  if (!href || typeof href !== 'string') return null;
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith('#') || /^(mailto|tel|javascript):/i.test(trimmed)) return null;
  let url;
  try {
    url = new URL(trimmed, origin);
  } catch (e) {
    return null;
  }
  if (url.origin !== origin) return null;
  const path = url.pathname;
  if (/\.[a-z0-9]{2,5}$/i.test(path)) return null; // a file, not a page
  // Already the query form (or any app route): as it is.
  if (url.search || !/^\/[a-z0-9-]+\/[^/]+\/[^/]+\/?$/i.test(path)) return path + url.search + url.hash;
  // The legacy two-segment detail: /<segment>/<rulebook--id>/<slug--id>/
  const [, segment, rulebook, slug] = path.replace(/\/+$/, '').split('/');
  if (!segment || !rulebook || !slug) return path;
  const params = new URLSearchParams();
  params.set('slug', slug.replace(LEGACY_ID, ''));
  params.set('rulebook', rulebook.replace(LEGACY_ID, ''));
  return `/${segment}?${params.toString()}${url.hash}`;
}

/**
 * Is this click one the app should route? Plain left click, no modifier (a
 * modified click is the reader asking for a new tab or window), an anchor
 * with an href, not marked to open elsewhere or download.
 */
export function shouldRouteClick(event, anchor) {
  if (!anchor || !anchor.getAttribute) return false;
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  const target = (anchor.getAttribute('target') || '').toLowerCase();
  if (target && target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;
  return !!anchor.getAttribute('href');
}
